import { SelectedExternalFile } from "@/lib/external-file-browser";
import { useState } from "react";
import { toast } from "sonner";
import { useKitsData } from "../data/use-kits-data";
import { groupKitTemplateControllerCreateTemplate } from "../lib/gen-api";
import { groupKitTemplateControllerCreateTemplate as brainCreateTemplate } from "../lib/gen-brain";
import { useLibraryExternalFileUploadAction } from "./use-library-external-file-upload-action";
import { useLibraryFileUploadAction } from "./use-library-file-upload-action";

type CreateEmptyTemplateArgs = {
  name: string;
};

type CreateTemplateWithContentArgs = {
  name: string;
  userContext: string;
  localFiles: File[];
  externalFiles: SelectedExternalFile[];
  type: "rpf" | "default";
};

export function useCreateTemplateCreatorAction(groupUuid: string | null) {
  const { createKit } = useKitsData(groupUuid, { type: "template" });
  const { upload: uploadLibraryFile } = useLibraryFileUploadAction(
    groupUuid,
    null,
  );
  const { upload: uploadLibraryExternalFile } =
    useLibraryExternalFileUploadAction({
      groupUuid,
      kitUuid: null,
    });
  const [isCreating, setIsCreating] = useState(false);

  async function createEmpty({
    name,
  }: CreateEmptyTemplateArgs): Promise<string | undefined> {
    setIsCreating(true);
    const kit = await createKit({ name });

    if (!kit) {
      toast.error("Failed to create kit. Please try again.");
      return;
    }

    await groupKitTemplateControllerCreateTemplate({
      path: { groupUuid: groupUuid!, kitUuid: kit.uuid },
      body: { name, scaffolding: "", instructions: "" },
    });

    toast.success("Template created successfully!");
    setIsCreating(false);
    return kit.uuid;
  }

  async function createWithContent({
    name,
    localFiles,
    externalFiles,
    userContext,
    type,
  }: CreateTemplateWithContentArgs) {
    setIsCreating(true);
    const kit = await createKit({ name });

    if (!kit) {
      toast.error("Failed to create kit. Please try again.");
      return;
    }

    // Upload files as library files
    const localFileUuids = await Promise.all(
      localFiles.map((file) =>
        uploadLibraryFile({ kitUuid: kit.uuid, file }).then((r) => r.uuid),
      ),
    );

    const externalFileUuids = await Promise.all(
      externalFiles.map((file) =>
        uploadLibraryExternalFile({ kitUuid: kit.uuid, file }).then(
          (r) => r.uuid,
        ),
      ),
    );

    const fileUuids = [...localFileUuids, ...externalFileUuids];

    await brainCreateTemplate({
      path: { kitUuid: kit.uuid, groupUuid: groupUuid! },
      body: { userContext, fileUuids, type },
    });

    setIsCreating(false);
    return kit.uuid;
  }

  return { createEmpty, createWithContent, isCreating };
}
